export interface CostCenter {
  id: number;
  code?: number | null; // Optional 4-digit code (0-9999)
  name: string;
  description?: string;
  status: CostCenterStatus;
  parent_id?: number | null;
  level: number;
  budgeted_cost?: number | null; // Estimated/budgeted cost for construction works
  created_at: Date;
  updated_at: Date;
  created_by?: string;
  is_active: boolean;
  user_id: string; // UUID from auth
}

export enum CostCenterStatus {
  ACTIVE = 'ativo',
  INACTIVE = 'inativo'
}

export interface CostCenterHierarchy extends CostCenter {
  children?: CostCenterHierarchy[];
  parent?: CostCenter;
  full_path: string;
  transactions_count?: number;
  total_amount?: number;
}

export interface CostCenterFilters {
  search?: string;
  status?: CostCenterStatus[];
  level?: number[];
  parent_id?: number;
  sort_by?: 'code' | 'name' | 'created_at' | 'status';
  sort_direction?: 'asc' | 'desc';
  show_inactive?: boolean;
}

export interface CostCenterFormData {
  code?: number | null; // Optional 4-digit code (0-9999)
  name: string;
  description?: string;
  parent_id?: number | null;
  status: CostCenterStatus;
  budgeted_cost?: number | null; // Estimated/budgeted cost
}

export interface CostCenterSummary {
  total_active: number;
  total_inactive: number;
  total_with_transactions: number;
  hierarchy_levels: number;
  most_used: CostCenter[];
}

export interface CostCenterValidation {
  code_exists: boolean;
  name_exists: boolean;
  valid_parent: boolean;
  max_level_reached: boolean;
}

export const COST_CENTER_STATUS_COLORS: Record<CostCenterStatus, string> = {
  [CostCenterStatus.ACTIVE]: 'text-green-600 bg-green-50 border-green-200',
  [CostCenterStatus.INACTIVE]: 'text-gray-600 bg-gray-50 border-gray-200'
};

export const COST_CENTER_STATUS_LABELS: Record<CostCenterStatus, string> = {
  [CostCenterStatus.ACTIVE]: 'Ativo',
  [CostCenterStatus.INACTIVE]: 'Inativo'
};

// Utility functions
export const validateCostCenterCode = (code?: number | string | null): boolean => {
  if (code === null || code === undefined) return true; // Code is optional
  const numericCode = typeof code === 'string' ? parseInt(code, 10) : code;
  return !isNaN(numericCode) && numericCode >= 0 && numericCode <= 9999;
};

// Format cost center code to 4-digit display (e.g., 1 -> "0001", 123 -> "0123")
export const formatCostCenterCode = (code?: number | null): string => {
  if (code === null || code === undefined) return '';
  return code.toString().padStart(4, '0');
};

export const generateCostCenterCode = (level: number, parentCode?: number | string): number => {
  if (level === 1) {
    return 0; // Root level starts with 0000
  }

  if (parentCode) {
    const parentNumeric = typeof parentCode === 'string' ? parseInt(parentCode, 10) : parentCode;
    const nextSequence = 1; // In a real app, this would be calculated from existing codes
    return parentNumeric * 100 + nextSequence; // Simple hierarchy generation
  }

  return 1; // Default to 0001
};

export const getCostCenterLevel = (code: number | string): number => {
  const codeStr = typeof code === 'number' ? formatCostCenterCode(code) : code;
  if (codeStr.length <= 2) return 1;
  return Math.floor(codeStr.length / 2);
};

export const buildCostCenterPath = (costCenter: CostCenter, allCostCenters: CostCenter[]): string => {
  const path: string[] = [];
  let current = costCenter;
  
  while (current) {
    path.unshift(current.name);
    if (current.parent_id) {
      const parent = allCostCenters.find(cc => cc.id === current.parent_id);
      current = parent || null;
    } else {
      break;
    }
  }
  
  return path.join(' > ');
};

export const buildHierarchy = (costCenters: CostCenter[]): CostCenterHierarchy[] => {
  const hierarchyMap = new Map<string, CostCenterHierarchy>();
  const rootItems: CostCenterHierarchy[] = [];
  
  // Create hierarchy items
  costCenters.forEach(cc => {
    const hierarchyItem: CostCenterHierarchy = {
      ...cc,
      children: [],
      full_path: buildCostCenterPath(cc, costCenters)
    };
    hierarchyMap.set(cc.id, hierarchyItem);
  });
  
  // Build parent-child relationships
  hierarchyMap.forEach(item => {
    if (item.parent_id) {
      const parent = hierarchyMap.get(item.parent_id);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(item);
        item.parent = parent;
      }
    } else {
      rootItems.push(item);
    }
  });
  
  return rootItems;
};

export const flattenHierarchy = (hierarchy: CostCenterHierarchy[]): CostCenterHierarchy[] => {
  const flattened: CostCenterHierarchy[] = [];
  
  const traverse = (items: CostCenterHierarchy[], depth = 0) => {
    items.forEach(item => {
      flattened.push({ ...item, level: depth + 1 });
      if (item.children && item.children.length > 0) {
        traverse(item.children, depth + 1);
      }
    });
  };
  
  traverse(hierarchy);
  return flattened;
};

export const validateCostCenter = (
  formData: CostCenterFormData,
  existingCostCenters: CostCenter[],
  editingId?: number
): CostCenterValidation => {
  const codeExists = existingCostCenters.some(cc =>
    cc.code !== null && formData.code !== null && cc.code === formData.code && cc.id !== editingId
  );

  const nameExists = existingCostCenters.some(cc =>
    cc.name.toLowerCase() === formData.name.toLowerCase() && cc.id !== editingId
  );

  let validParent = true;
  let maxLevelReached = false;

  if (formData.parent_id) {
    const parent = existingCostCenters.find(cc => cc.id === formData.parent_id);
    validParent = !!parent && parent.status === CostCenterStatus.ACTIVE;

    if (parent && parent.code !== null) {
      const parentLevel = getCostCenterLevel(parent.code);
      maxLevelReached = parentLevel >= 5; // Maximum 5 levels
    }
  }

  return {
    code_exists: codeExists,
    name_exists: nameExists,
    valid_parent: validParent,
    max_level_reached: maxLevelReached
  };
};

export const getAvailableParents = (
  costCenters: CostCenter[],
  excludeId?: number
): CostCenter[] => {
  return costCenters.filter(cc =>
    cc.status === CostCenterStatus.ACTIVE &&
    cc.id !== excludeId &&
    (cc.code === null || getCostCenterLevel(cc.code) < 5) // Can only be parent if not at max level
  );
};